import {
  useEffect,
  useId,
  useState,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

import { ExternalRegisterDot } from "@/components/externalregister/ExternalRegisterDot";
import { getPatientExternalRegisterAlerts } from "@/components/externalregister/patientSummary";
import type { ExternalRegisterPatientSummary } from "@/components/externalregister/types";
import type { Patient } from "@/resources/types";
import { patientService } from "@/services";

type PatientSearchResultPatient = Patient & Partial<ExternalRegisterPatientSummary>;

export const getPatientSearchDisplayName = (patient: Patient): string => {
  return [patient.firstName, patient.lastName].filter(Boolean).join(" ") || "Unnamed patient";
};

export const getPatientSearchSecondarySummary = (patient: Patient): string => {
  return [
    patient.umrn ? `UMRN ${patient.umrn}` : undefined,
    patient.dateOfBirth ? `DOB ${patient.dateOfBirth}` : undefined,
    patient.id,
  ].filter(Boolean).join(" · ");
};

const getPatientSearchDetails = (patient: Patient): Array<{ label: string; value: string }> => [
  { label: "UMRN", value: patient.umrn ?? "" },
  { label: "DOB", value: patient.dateOfBirth ?? "" },
  { label: "Sex", value: patient.sex ?? "" },
  { label: "Medicare", value: [patient.medicareNo, patient.medicareNoPos ? `IRN ${patient.medicareNoPos}` : undefined].filter(Boolean).join(" · ") },
  { label: "Phone", value: patient.phone ?? "" },
  { label: "Email", value: patient.email ?? "" },
].filter((detail) => detail.value);

const PatientSearchExternalRegisterIndicator = ({
  patient,
}: {
  patient: PatientSearchResultPatient;
}) => {
  const alerts = getPatientExternalRegisterAlerts(patient);
  if (!alerts.length) return null;

  return (
    <ExternalRegisterDot alerts={alerts} />
  );
};

const PatientSearchDetailGrid = ({ patient }: { patient: Patient }) => {
  const details = getPatientSearchDetails(patient);
  if (!details.length) return null;

  return (
    <Box sx={{
      display: "grid",
      gap: 0.75,
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, minmax(0, 1fr))",
        md: "repeat(3, minmax(0, 1fr))",
      },
      mt: 0.75,
    }}>
      {details.map((detail) => (
        <Box
          key={`${detail.label}-${detail.value}`}
          sx={{
            minWidth: 0,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            px: 0.75,
            py: 0.5,
            backgroundColor: "background.paper",
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 800, lineHeight: 1.1 }}>
            {detail.label}
          </Typography>
          <Typography variant="body2" noWrap title={detail.value} sx={{ fontWeight: 700, lineHeight: 1.25 }}>
            {detail.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const PatientSearchResultContent = ({ patient }: { patient: PatientSearchResultPatient }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography variant="body2" noWrap sx={{ fontWeight: 900, minWidth: 0 }}>
        {getPatientSearchDisplayName(patient)}
      </Typography>
      <PatientSearchExternalRegisterIndicator patient={patient} />
    </Stack>
    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
      Patient ID {patient.id}
    </Typography>
    <PatientSearchDetailGrid patient={patient} />
  </Box>
);

export const PatientSearchPicker = ({
  selectedPatientId,
  onSelectPatient,
  label = "Search patient",
  selectedLabel = "Selected patient",
  ariaLabel = "Patient search",
  required = false,
  disabled = false,
  helperText = "Search by name, UMRN, or patient identifier.",
  textFieldSx,
  selectButtonSx,
}: {
  selectedPatientId: string;
  onSelectPatient: (patientId: string, patient: Patient | null) => void;
  label?: string;
  selectedLabel?: string;
  ariaLabel?: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  textFieldSx?: SxProps<Theme>;
  selectButtonSx?: SxProps<Theme>;
}) => {
  const inputId = useId().replace(/[^a-z0-9-]/gi, "-");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPatient, setSelectedPatient] = useState<PatientSearchResultPatient | null>(null);
  const [patients, setPatients] = useState<PatientSearchResultPatient[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    if (!selectedPatientId && selectedPatient) {
      setSelectedPatient(null);
      setSearchTerm("");
    }
  }, [selectedPatient, selectedPatientId]);

  useEffect(() => {
    if (selectedPatient) {
      setPatients([]);
      setLoading(false);
      return undefined;
    }

    const query = searchTerm.trim();
    if (query.length < 2) {
      setPatients([]);
      setLoading(false);
      setHasSearched(false);
      return undefined;
    }

    let cancelled = false;
    const timeout = window.setTimeout(() => {
      setLoading(true);
      setHasSearched(true);
      void patientService.getList({
        search: `*${query}*`,
        sort: {
          field: "lastModified",
          order: "desc",
        },
      } as never).then((response) => {
        if (!cancelled) {
          setPatients((response.data ?? []) as PatientSearchResultPatient[]);
        }
      }).catch(() => {
        if (!cancelled) {
          setPatients([]);
        }
      }).finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [searchTerm, selectedPatient]);

  const searchReady = searchTerm.trim().length >= 2 && !selectedPatient;

  const selectPatient = (patient: PatientSearchResultPatient) => {
    setSelectedPatient(patient);
    setSearchTerm(getPatientSearchDisplayName(patient));
    setPatients([]);
    onSelectPatient(patient.id, patient);
  };

  return (
    <Stack spacing={1}>
      <Box component="form" autoComplete="off" onSubmit={(event) => event.preventDefault()}>
        <TextField
          id={`patient-search-${inputId}`}
          name={`patient-search-no-autofill-${inputId}`}
          label={label}
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
            setSelectedPatient(null);
            onSelectPatient("", null);
          }}
          disabled={disabled || loading}
          required={required}
          autoComplete="off"
          inputProps={{
            "aria-label": ariaLabel,
            autoComplete: "off",
            autoCorrect: "off",
            autoCapitalize: "off",
            spellCheck: false,
            name: `patient-search-no-autofill-${inputId}`,
            id: `patient-search-input-${inputId}`,
            "data-lpignore": "true",
            "data-form-type": "other",
          }}
          helperText={loading ? "Searching patients..." : helperText}
          sx={textFieldSx}
        />
      </Box>
      {selectedPatient && selectedPatientId ? (
        <Paper variant="outlined" sx={{ p: 1, borderRadius: 1, backgroundColor: "grey.50" }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 700, textTransform: "uppercase" }}>
            {selectedLabel}
          </Typography>
          <PatientSearchResultContent patient={selectedPatient} />
        </Paper>
      ) : null}
      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">Searching patients...</Typography>
        </Stack>
      ) : null}
      {searchReady && hasSearched && !loading ? (
        <Paper variant="outlined" sx={{ borderRadius: 1, maxHeight: 240, overflow: "auto" }}>
          {patients.length ? (
            <Stack divider={<Divider />}>
              {patients.slice(0, 8).map((patient) => (
                <Stack
                  key={patient.id}
                  role="button"
                  tabIndex={0}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1.5}
                  onClick={() => selectPatient(patient)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      selectPatient(patient);
                    }
                  }}
                  sx={{
                    px: 1,
                    py: 1,
                    cursor: "pointer",
                    transition: "background-color 120ms ease",
                    "&:hover, &:focus-visible": {
                      backgroundColor: "action.hover",
                      outline: "none",
                    },
                  }}
                >
                  <PatientSearchResultContent patient={patient} />
                  <Button
                    variant="outlined"
                    size="small"
                    sx={selectButtonSx}
                    onClick={(event) => {
                      event.stopPropagation();
                      selectPatient(patient);
                    }}
                  >
                    Select
                  </Button>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.75 }}>
              No patients matched this search.
            </Typography>
          )}
        </Paper>
      ) : null}
    </Stack>
  );
};
